import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/browser';
import { useAuth0 } from '@auth0/auth0-react';
import { useDebounce } from '@/hooks/useDebounce';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  DeleteOutlineRounded as DeleteIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { accordionStyles, inviteScrollDesign, usersLengthStyle } from '../../styles';
import { abyssColors } from '@/theme/colors';
import * as state from '@/state';
import {
  PreRegisteredUsersDocument,
  useDeletePreRegisteredUserMutation,
  usePreRegisteredUsersQuery,
  PreRegisteredUsersQuery,
  PageInfo,
} from '@/__generated__/graphql';
import { useAtom } from 'jotai';

type PreRegisteredUsersType = PreRegisteredUsersQuery['preRegisteredUsers'];

export const PreRegisteredUsers = () => {
  const currentUser = useAuth0()?.user;

  const inputReference = useRef<HTMLInputElement>(null);
  const stackReference = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm, 700);
  const [pageInfo, setPageInfo] = useState<PageInfo>({ limit: 5, skip: 0 });
  const [paginatedUsers, setPaginatedUsers] = useState<PreRegisteredUsersType>([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [deletePreRegisteredUser] = useDeletePreRegisteredUserMutation();
  const [deleteLoading, setDeleteLoading] = useState<Record<string, boolean>>({});
  const [, setSnackbarMessage] = useAtom(state.snackbarMessage);

  const { data: preRegisteredUsersData, loading } = usePreRegisteredUsersQuery({
    variables: {
      pageInfo,
      input: {
        searchTerm: debouncedSearchTerm,
      },
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (preRegisteredUsersData?.preRegisteredUsers) {
      const { preRegisteredUsers, preRegisteredUsersCount } = preRegisteredUsersData;
      if (pageInfo.skip === 0) {
        setPaginatedUsers(preRegisteredUsers);
      } else {
        setPaginatedUsers((previousUsers) => [
          ...new Set([...previousUsers, ...preRegisteredUsers]),
        ]);
      }
      setTotalUsers(preRegisteredUsersCount?.count || 0);
    }
  }, [pageInfo.skip, preRegisteredUsersData, debouncedSearchTerm]);

  useEffect(() => {
    setPageInfo((previousPageInfo) => ({
      ...previousPageInfo,
      skip: 0,
    }));
    if (stackReference?.current) {
      stackReference.current.scrollTop = 0;
    }
  }, [debouncedSearchTerm]);

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    [setSearchTerm]
  );

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    if (inputReference?.current) {
      inputReference.current.value = '';
    }
  }, [setSearchTerm, inputReference]);

  const handleAccordionChange = useCallback(
    (isExpanded: boolean) => {
      if (!isExpanded) {
        clearSearch();
      }
    },
    [clearSearch]
  );

  const handleStackScroll = useCallback(() => {
    const stackElement = stackReference.current;
    if (stackElement) {
      const isAtBottom =
        stackElement.scrollHeight - stackElement.scrollTop === stackElement.clientHeight;
      const { skip } = pageInfo || {};
      if (skip !== null && skip !== undefined && isAtBottom && !loading && skip < totalUsers) {
        setPageInfo((previousPageInfo) => ({
          ...previousPageInfo,
          skip: (previousPageInfo?.skip ?? 0) + (previousPageInfo?.limit ?? 0),
        }));
      }
    }
  }, [stackReference, loading, pageInfo, totalUsers, setPageInfo]);

  useEffect(() => {
    window.addEventListener('scroll', handleStackScroll);
    return () => {
      window.removeEventListener('scroll', handleStackScroll);
    };
  }, [handleStackScroll]);

  const showSnackbarMessage = (
    message: string,
    severity: 'error' | 'info' | 'success' | 'warning'
  ) => {
    return (
      <Alert variant="filled" severity={severity}>
        {message && (
          <>
            {message}
            &nbsp;&nbsp;
          </>
        )}
      </Alert>
    );
  };

  const handleDeletePreRegisteredUser = async (id: string) => {
    setDeleteLoading((previous) => ({ ...previous, [id]: true }));
    setPaginatedUsers((previousUsers) => previousUsers.filter((user) => user.id !== id));
    await deletePreRegisteredUser({
      variables: {
        userId: id,
      },
      refetchQueries: [PreRegisteredUsersDocument],
      onCompleted: () => {
        setSnackbarMessage({
          shouldShow: true,
          content: showSnackbarMessage('Deleted SSO User Successfully', 'success'),
        });
      },
      onError: (error) => {
        Sentry.captureMessage(`Failed to delete SSO user: ${error.message}`, {
          user: { sub: currentUser?.sub },
          extra: { userId: id },
        });
        setSnackbarMessage({
          shouldShow: true,
          content: showSnackbarMessage(`Failed to delete SSO user: ${error.message}`, 'error'),
        });
      },
    });
    setDeleteLoading((previous) => ({ ...previous, [id]: false }));
  };

  return (
    <Accordion
      onChange={(_, isExpanded) => handleAccordionChange(isExpanded)}
      disableGutters
      sx={accordionStyles.root}
    >
      <AccordionSummary sx={accordionStyles.summary} expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body1" fontWeight="bold">
            Single Sign-On Users
          </Typography>
          <Box sx={usersLengthStyle}>{totalUsers}</Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ m: 0 }}>
        <TextField
          fullWidth
          label="Search Sign-On Users"
          variant="standard"
          sx={{ mb: 2 }}
          inputRef={inputReference}
          onChange={handleSearch}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searchTerm ? (
                  <ClearIcon sx={{ cursor: 'pointer' }} onClick={clearSearch} />
                ) : (
                  <SearchIcon />
                )}
              </InputAdornment>
            ),
          }}
        />
        {loading && <LinearProgress sx={{ mb: 1 }} />}
        <Stack
          spacing={1}
          ref={stackReference}
          onScroll={handleStackScroll}
          sx={{
            maxHeight: '120px',
            overflowY: 'scroll',
            pr: 1,
            ...inviteScrollDesign,
          }}
        >
          {paginatedUsers?.map((user) => (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              key={user.id}
              sx={{
                border: '1px solid #F0F0F0',
                p: '8px',
                borderRadius: '4px',
              }}
            >
              <Avatar alt={user?.email}>{user.email[0].toUpperCase()}</Avatar>
              <Typography flex={1}>{user.email}</Typography>
              {deleteLoading[user.id] ? (
                <CircularProgress size={20} />
              ) : (
                <IconButton onClick={() => handleDeletePreRegisteredUser(user.id)}>
                  <DeleteIcon style={{ color: abyssColors.primary[700] }} />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
